import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, WHATSAPP_LINK } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Check, Loader2, ShieldCheck } from "lucide-react";

const MODES = ["Phone call", "Video call (Meet/Zoom)", "WhatsApp or text chat"];
const initial = {
  full_name: "", email: "", contact_number: "", preferred_day: "",
  preferred_time: "", session_mode: "", message: "", under_18_consent: false,
};

const BookingForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.contact_number || !form.preferred_day || !form.preferred_time || !form.session_mode) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Your request has been received. We'll reach out on WhatsApp soon.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="rounded-3xl bg-card border border-border p-10 text-center">
        <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-primary/15 text-primary">
          <Check className="w-8 h-8" />
        </div>
        <h3 className="font-serif text-2xl mb-3">Thank you for reaching out.</h3>
        <p className="text-muted-foreground max-w-md mx-auto mb-6">
          Your request has been received. We'll connect with you privately on WhatsApp to confirm a time
          and share payment details. There's no rush — take your time.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button className="rounded-full px-6 bg-[#25D366] hover:bg-[#20bd5a]" data-testid="success-whatsapp-button">
              <MessageCircle className="w-4 h-4 mr-2" /> Chat with us now
            </Button>
          </a>
          <Button variant="outline" className="rounded-full px-6" onClick={() => setDone(false)} data-testid="new-request-button">
            Send another request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="booking-form" className="rounded-3xl bg-card border border-border p-6 sm:p-10 space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="full_name">Full name *</Label>
          <Input id="full_name" data-testid="input-full-name" className="rounded-xl h-12 bg-background" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="How should we call you?" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" data-testid="input-email" className="rounded-xl h-12 bg-background" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact_number">WhatsApp / contact number *</Label>
          <Input id="contact_number" data-testid="input-contact-number" className="rounded-xl h-12 bg-background" value={form.contact_number} onChange={(e) => set("contact_number", e.target.value)} placeholder="Your number" />
        </div>
        <div className="space-y-2">
          <Label>Preferred session mode *</Label>
          <Select value={form.session_mode} onValueChange={(v) => set("session_mode", v)}>
            <SelectTrigger data-testid="select-session-mode" className="rounded-xl h-12 bg-background">
              <SelectValue placeholder="Choose how you'd like to talk" />
            </SelectTrigger>
            <SelectContent>
              {MODES.map((m) => (
                <SelectItem key={m} value={m} data-testid={`mode-option-${m}`}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="preferred_day">Preferred day *</Label>
          <Input id="preferred_day" data-testid="input-preferred-day" className="rounded-xl h-12 bg-background" value={form.preferred_day} onChange={(e) => set("preferred_day", e.target.value)} placeholder="e.g. This Saturday" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="preferred_time">Preferred time slot *</Label>
          <Input id="preferred_time" data-testid="input-preferred-time" className="rounded-xl h-12 bg-background" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} placeholder="e.g. Evening, after 6pm" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">What would you like to talk about? (optional)</Label>
        <Textarea id="message" data-testid="input-message" className="rounded-xl bg-background min-h-28" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Share as much or as little as you like. This is a safe space." />
      </div>

      <label className="flex items-start gap-3 rounded-xl bg-muted p-4 cursor-pointer" data-testid="consent-wrapper">
        <Checkbox checked={form.under_18_consent} onCheckedChange={(v) => set("under_18_consent", !!v)} data-testid="checkbox-under-18" className="mt-0.5" />
        <span className="text-sm text-muted-foreground leading-relaxed">
          If I am under 18, I confirm I have a parent or guardian's consent before booking.
        </span>
      </label>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="w-4 h-4 text-primary" />
        No payment is taken here. We arrange everything privately over WhatsApp.
      </div>

      <Button type="submit" disabled={loading} data-testid="submit-booking-button" className="rounded-full w-full h-12 text-base">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request my session"}
      </Button>
    </form>
  );
};

export default BookingForm;
